import React from 'react';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm/CustomNewConfirm';
import CustomInput from 'components/common/CustomInput';
import CustomDatePicker from 'components/common/CustomDatePicker';
import PropTypes from 'prop-types';
import { dateFieldToLocalDate } from 'utils/time';

const SeasonFormModal = ({ isOpen, onClose, season, activeSeason, onSave }) => {
  const [formData, setFormData] = React.useState({
    name: '',
    startDate: '',
    endDate: '',
  });
  const [errors, setErrors] = React.useState({});

  React.useEffect(() => {
    if (season) {
      // The dates exactly as the server stores them. They were read through new Date(...).toISOString(), which moved
      // them to the day before in any time zone east of UTC, and saving then stored that day (2026-10-20).
      setFormData({
        name: season.name,
        startDate: season.startDateValue || '',
        endDate: season.endDateValue || '',
      });
    } else {
      setFormData({
        name: '',
        startDate: '',
        endDate: '',
      });
    }
    setErrors({});
  }, [season, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    const newErrors = validateForm();
    setErrors(newErrors);
  };

  const validateForm = () => {
    const newErrors = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!formData.name.trim()) {
      newErrors.name = 'Season name is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }

    // Every date here is local midnight of its day, so a typed date, today and each limit compare as days.
    const startDate = dateFieldToLocalDate(formData.startDate);
    const endDate = dateFieldToLocalDate(formData.endDate);
    if (startDate && endDate) {
      if (startDate >= endDate) {
        newErrors.endDate = 'End date must be after start date';
      }

      if (season?.status === 'Active') {
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        if (endDate < tomorrow) {
          newErrors.endDate = 'End date cannot be earlier than tomorrow';
        }

        const startYear = startDate.getFullYear();
        const maxEndDate = new Date(startYear + 1, 11, 30);

        if (endDate > maxEndDate) {
          newErrors.endDate =
            'End date cannot extend beyond Dec 30 of the year following the start year';
        }
      }
      if (!season || season.status === 'Draft') {
        const activeEndDate = dateFieldToLocalDate(activeSeason?.endDateValue);
        if (activeEndDate) {
          activeEndDate.setDate(activeEndDate.getDate() + 1);

          if (startDate < activeEndDate) {
            newErrors.startDate = `Must start at least 1 day after current active season ends (${activeEndDate.toLocaleDateString()})`;
          }
        }
      }
    }

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validateForm();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSave(formData);
  };

  const getValidationRules = () => {
    if (season?.status === 'Historical') {
      return ['Historical seasons cannot be modified'];
    }

    if (season?.status === 'Active') {
      return [
        'Start date cannot be changed for active seasons',
        'End date can be extended or reduced within the same competition year',
        'End date cannot extend beyond Dec 30 of the year following start year',
        'End date cannot be earlier than tomorrow',
      ];
    }

    return [
      'Must start at least 1 day after current active season ends',
      'Cannot overlap with existing seasons',
      'End date must be after start date',
      'Only one draft season allowed at a time',
    ];
  };

  return (
    <CustomNewConfirm
      isOpen={isOpen}
      onClose={onClose}
      title={season ? `Edit ${season.name}` : 'Add New Season'}
      onConfirm={handleSubmit}
      confirmButtonText="Save Season"
      showValidationRules={true}
      validationRules={getValidationRules()}
    >
      <form onSubmit={handleSubmit}>
        <CustomInput
          label="Season Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          error={errors.name}
          placeholder="e.g., 25-26 Winter"
          required
        />
        <CustomDatePicker
          label="Start Date"
          name="startDate"
          value={formData.startDate}
          onChange={handleChange}
          error={errors.startDate}
          disabled={
            season?.status === 'Active' || season?.status === 'Historical'
          }
          required
        />
        <CustomDatePicker
          label="End Date"
          name="endDate"
          value={formData.endDate}
          onChange={handleChange}
          error={errors.endDate}
          required
        />
      </form>
    </CustomNewConfirm>
  );
};

SeasonFormModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  season: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    startDateValue: PropTypes.string,
    endDateValue: PropTypes.string,
    status: PropTypes.oneOf(['Draft', 'Active', 'Historical']),
    isActive: PropTypes.bool,
  }),
  activeSeason: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    endDateValue: PropTypes.string,
    status: PropTypes.oneOf(['Draft', 'Active', 'Historical']),
    isActive: PropTypes.bool,
  }),
  onSave: PropTypes.func.isRequired,
  validationRules: PropTypes.arrayOf(PropTypes.string),
};

export default SeasonFormModal;
